import { useEffect, useMemo, useState } from "react";
import { Empty, Spin } from "antd";
import { getCraResult, type CustomerDTO } from "@ifa/utils";

import formStyles from "./ZuuRiskAssessmentSection.module.css";
import sectionStyles from "./ZuuSection.module.css";
import {
  formatZuuRiskValue,
  parseZuuRiskProjectResults,
  type ZuuRiskValueKind,
} from "./zuuRiskMapping.js";

type RiskProjectSection = {
  title: string;
  badge: string;
  project: string;
};

const riskProjectSections: RiskProjectSection[] = [
  {
    title: "身份核實",
    badge: "Identity",
    project: "VERIFICATION_OF_IDENTITY",
  },
  {
    title: "國家風險",
    badge: "Country",
    project: "COUNTRY_RISK",
  },
  {
    title: "職業資料",
    badge: "Industry",
    project: "OCCUPATION_DETAILS",
  },
  {
    title: "資金和財富來源",
    badge: "Funds",
    project: "SOURCE_OF_FOUND_AND_WEALTH",
  },
  {
    title: "交易活動",
    badge: "Activity",
    project: "TRANSACTION",
  },
  {
    title: "產品風險（只適用於人壽保險）",
    badge: "Product",
    project: "PRODUCT_RISK",
  },
  {
    title: "政治人物 / 恐怖分子識別",
    badge: "PEP",
    project: "IDENTIFICATION_PEP_AND_TERRORISTS",
  },
  {
    title: "風險結論",
    badge: "Summary",
    project: "FINAL_AML_CFT_RISK_ASSESSMENT",
  },
];

export function ZuuRiskAssessmentSection({ customer }: { customer: CustomerDTO | null }) {
  const [loading, setLoading] = useState(false);
  const [riskData, setRiskData] = useState<unknown>(null);

  useEffect(() => {
    if (!customer?.sourceOrderId) {
      setRiskData(null);
      return;
    }

    let active = true;
    setLoading(true);
    getCraResult(customer.sourceOrderId)
      .then((response) => {
        if (active) setRiskData(response);
      })
      .catch((error) => {
        console.error("Failed to fetch ZUU CRA data:", error);
        if (active) setRiskData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [customer?.sourceOrderId]);

  const projectResults = useMemo(() => parseZuuRiskProjectResults(riskData), [riskData]);
  const projectResultMap = useMemo(
    () => new Map(projectResults.map((item) => [item.project, item])),
    [projectResults],
  );

  return (
    <section className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <div>
          <h3 className={sectionStyles.title}>風險評估</h3>
          <div className={sectionStyles.subtitle}>
            依最近來源訂單讀取 CRA 分項結果，按 AML/CFT 風險項目查看最終等級。
          </div>
        </div>
        <div className={sectionStyles.badge}>
          {customer?.sourceOrderId ? `訂單 #${customer.sourceOrderId}` : "無來源訂單"}
        </div>
      </div>

      {loading ? (
        <div className={sectionStyles.empty}>
          <Spin />
        </div>
      ) : !customer?.sourceOrderId ? (
        <div className={sectionStyles.empty}>
          <Empty description="沒有可用的風險評估來源訂單" />
        </div>
      ) : projectResults.length ? (
        <div className={formStyles.groups}>
          {riskProjectSections.map((section) => {
            const result = projectResultMap.get(section.project);
            const displayValue = result ? formatZuuRiskValue(result.result, "riskLevel") : "--";
            const toneClass = getAnswerToneClass("riskLevel", displayValue);

            return (
              <div key={section.project} className={formStyles.group}>
                <div className={formStyles.groupHeader}>
                  <div>
                    <h4 className={formStyles.groupTitle}>{section.title}</h4>
                    <div className={formStyles.groupSubtitle}>
                      依 CRA 項目結果查看風險等級與原因說明。
                    </div>
                  </div>
                  <div className={formStyles.groupBadge}>{section.badge}</div>
                </div>
                <div className={formStyles.questionList}>
                  <div className={formStyles.questionRow}>
                    <div className={formStyles.questionLabel}>評估結果</div>
                    <div className={formStyles.answerWrap}>
                      <span className={`${formStyles.answerPill} ${toneClass}`}>{displayValue}</span>
                      <div className={formStyles.answerMeta}>
                        {result?.reason?.trim()
                          ? `原因：${result.reason}`
                          : result
                            ? `來源項目：${result.project}`
                            : "暫無評估結果"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={sectionStyles.empty}>
          <Empty description="暫無 CRA 分項評估結果" />
        </div>
      )}
    </section>
  );
}

function getAnswerToneClass(kind: ZuuRiskValueKind | undefined, displayValue: string) {
  if (displayValue === "--") return formStyles.pillEmpty;

  if (kind === "yesNo") {
    return displayValue === "是" ? formStyles.pillPositive : formStyles.pillNegative;
  }

  if (kind === "riskLevel") {
    if (displayValue.includes("低")) return formStyles.pillPositive;
    if (displayValue.includes("中")) return formStyles.pillWarning;
    if (displayValue.includes("高")) return formStyles.pillNegative;
  }

  return formStyles.pillNeutral;
}
